import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";

/**
 * Text input with a "as-you-type" suggestion popover.
 * - `options`: string[] — existing client names to suggest
 * - `value` / `onChange` — controlled input
 * User may pick an option OR keep typing a brand-new value.
 */
export default function ClientAutocomplete({ value, onChange, options = [], placeholder = "Enter client name…", required, testid }) {
  const [focused, setFocused] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setFocused(false);
    };
    if (focused) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [focused]);

  const uniq = Array.from(new Set((options || []).map((s) => (s || "").trim()).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b));
  const q = (value || "").trim().toLowerCase();
  const matches = q
    ? uniq.filter((n) => n.toLowerCase().includes(q) && n.toLowerCase() !== q)
    : uniq;
  const showList = focused && matches.length > 0;

  return (
    <div className="relative" ref={wrapperRef}>
      <Input
        data-testid={testid}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        autoComplete="off"
        className="rounded-none"
      />
      {showList && (
        <div
          data-testid={testid ? `${testid}-suggestions` : "client-suggestions"}
          className="absolute z-50 left-0 right-0 mt-0 max-h-56 overflow-y-auto bg-white border border-slate-300 shadow-lg"
        >
          {matches.map((opt) => (
            <button
              key={opt}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange(opt); setFocused(false); }}
              className="block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100"
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
